/**
 * ContentSuggestions - AI-powered content tip bar for the Dashboard.
 *
 * Displays a single personalized carousel suggestion in a compact horizontal bar.
 * Uses a 24h cache stored in profile preferences (JSONB) to avoid unnecessary API calls.
 * Invalidates cache when avatar data changes (via simple hash comparison).
 */

import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from '@/contexts/LanguageContext';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { Sparkles, ArrowRight, Users } from 'lucide-react';
import { generateContentSuggestions, type ContentSuggestion, type CachedSuggestions } from '@/services/geminiService';
import { saveProfile, type ProfilePreferences, type UserProfile } from '@/services/profileService';
import type { AvatarData } from '@/types';

// ============================================================================
// CONSTANTS & HELPERS
// ============================================================================

const CACHE_TTL_MS = 24 * 60 * 60 * 1000; // 24 hours

/**
 * Simple hash of avatar data to detect changes.
 */
function computeAvatarHash(avatar: AvatarData): string {
  const key = [
    avatar.magneticBio || '',
    avatar.publicoAlvo || '',
    avatar.bioDescription || '',
    avatar.nicho || '',
    (avatar.doresIdentificadas || []).join(','),
    (avatar.desejosIdentificados || []).join(','),
  ].join('|');
  let hash = 5381;
  for (let i = 0; i < key.length; i++) {
    hash = ((hash << 5) + hash) + key.charCodeAt(i);
  }
  return (hash >>> 0).toString(36);
}

function isCacheValid(cache: CachedSuggestions | undefined, currentAvatarHash: string): boolean {
  if (!cache || !cache.suggestions || cache.suggestions.length === 0) return false;
  if (!cache.generatedAt) return false;
  const elapsed = Date.now() - new Date(cache.generatedAt).getTime();
  if (elapsed > CACHE_TTL_MS) return false;
  if (cache.avatarHash && cache.avatarHash !== currentAvatarHash) return false;
  return true;
}

// ============================================================================
// COMPONENT
// ============================================================================

interface ContentSuggestionsProps {
  avatarData?: AvatarData;
  preferences: ProfilePreferences | null;
  userId: string;
  userProfile: UserProfile | null;
  onPreferencesUpdated: (prefs: ProfilePreferences) => void;
  compact?: boolean;
  className?: string;
  onPick?: (topic: string) => void;
}

const ContentSuggestions: React.FC<ContentSuggestionsProps> = ({
  avatarData,
  preferences,
  userId,
  userProfile,
  onPreferencesUpdated,
  compact = false,
  className,
  onPick,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [suggestion, setSuggestion] = useState<ContentSuggestion | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const hasFetched = useRef(false);

  // ---- Auto-load on mount: check cache or generate ----
  useEffect(() => {
    if (hasFetched.current) return;
    if (!avatarData) return;

    const avatarHash = computeAvatarHash(avatarData);
    const cached = preferences?.contentSuggestions;

    if (isCacheValid(cached, avatarHash)) {
      setSuggestion(cached!.suggestions[0] || null);
      hasFetched.current = true;
    } else {
      hasFetched.current = true;
      handleGenerate();
    }
  }, [avatarData, preferences?.contentSuggestions]); // eslint-disable-line react-hooks/exhaustive-deps

  // ---- Generate suggestion ----
  const handleGenerate = async () => {
    if (!avatarData || !userId || !userProfile) return;
    if (loading) return;

    setLoading(true);
    setError(null);

    try {
      const newSuggestions = await generateContentSuggestions(avatarData);
      const first = newSuggestions[0] || null;
      setSuggestion(first);

      // Save to cache
      const avatarHash = computeAvatarHash(avatarData);
      const cachedData: CachedSuggestions = {
        suggestions: newSuggestions,
        generatedAt: new Date().toISOString(),
        avatarHash,
      };

      const updatedPrefs: ProfilePreferences = {
        ...preferences,
        contentSuggestions: cachedData,
      };

      try {
        await saveProfile(
          userId,
          { name: userProfile.name, handle: userProfile.handle, avatarUrl: userProfile.avatarUrl },
          updatedPrefs
        );
        onPreferencesUpdated(updatedPrefs);
      } catch (saveErr) {
        // Failed to cache suggestions - non-blocking
      }
    } catch (err: any) {
      // Generation failed - error shown to user via setError
      setError(t('contentSuggestions.generateFailed'));
    } finally {
      setLoading(false);
    }
  };

  // ---- Click: either hand the topic to the parent or navigate to /create ----
  const handleCreateClick = () => {
    if (!suggestion) return;
    if (onPick) {
      onPick(suggestion.topic);
      return;
    }
    navigate('/create', { state: { prefillTopic: suggestion.topic } });
  };

  // ---- EMPTY STATE: no avatar configured ----
  if (!avatarData) {
    return (
      <div className={cn(compact ? 'mb-0' : 'mb-8', className)}>
        <div className="bg-muted/50 backdrop-blur-sm rounded-xl px-4 md:px-6 py-4 flex items-center gap-3 md:gap-4 border border-dashed border-border">
          <div className="w-10 h-10 md:w-12 md:h-12 rounded-full bg-muted flex items-center justify-center flex-shrink-0">
            <Users className="h-4 w-4 md:h-5 md:w-5 text-muted-foreground" />
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="text-sm md:text-base font-bold text-foreground mb-0.5">
              {t('contentSuggestions.noAvatar')}
            </h3>
            <p className="text-xs md:text-sm text-muted-foreground break-words">
              {t('contentSuggestions.noAvatarDesc')}
            </p>
          </div>
        </div>
      </div>
    );
  }

  // ---- LOADING STATE: skeleton bar ----
  if (loading && !suggestion) {
    return (
      <div className={cn(compact ? 'mb-0' : 'mb-8', className)}>
        <div className="bg-muted/50 backdrop-blur-sm rounded-xl px-4 md:px-6 py-4 flex flex-col md:flex-row md:items-center md:justify-between gap-3 md:gap-4 animate-pulse">
          <div className="flex items-center gap-3 md:gap-4 flex-1 min-w-0">
            <div className="w-10 h-10 md:w-12 md:h-12 rounded-full bg-muted flex-shrink-0" />
            <div className="flex-1 min-w-0">
              <div className="h-4 bg-muted rounded w-32 md:w-40 mb-2" />
              <div className="h-3.5 bg-muted rounded w-48 md:w-64" />
            </div>
          </div>
          <div className="h-9 w-full md:w-28 bg-muted rounded-lg flex-shrink-0" />
        </div>
      </div>
    );
  }

  // ---- ERROR STATE ----
  if (error && !suggestion) {
    return (
      <div className={cn(compact ? 'mb-0' : 'mb-8', className)}>
        <div className="bg-muted/50 backdrop-blur-sm rounded-xl px-4 md:px-6 py-4 flex items-center gap-3 md:gap-4 border border-destructive/20">
          <div className="flex items-center gap-3 md:gap-4 flex-1 min-w-0">
            <div className="w-10 h-10 md:w-12 md:h-12 rounded-full bg-primary flex items-center justify-center flex-shrink-0 shadow-lg shadow-primary/20">
              <Sparkles className="h-4 w-4 md:h-5 md:w-5 text-primary-foreground" />
            </div>
            <div className="flex-1 min-w-0">
              <h3 className="text-sm md:text-base font-bold text-foreground mb-0.5">
                {t('contentSuggestions.title')}
              </h3>
              <p className="text-xs md:text-sm text-destructive">{error}</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ---- NORMAL STATE: no suggestion yet ----
  if (!suggestion) return null;

  // ---- MAIN STATE: single suggestion bar ----
  return (
    <div className={cn(compact ? 'mb-0' : 'mb-8', className)}>
      <div className="bg-muted/50 backdrop-blur-sm rounded-xl px-4 md:px-6 py-4 flex flex-col md:flex-row md:items-center md:justify-between gap-3 md:gap-4">
        {/* Left: icon + text */}
        <div className="flex items-center gap-3 md:gap-4 flex-1 min-w-0">
          <div className="w-10 h-10 md:w-12 md:h-12 rounded-full bg-primary flex items-center justify-center flex-shrink-0 shadow-lg shadow-primary/20">
            <Sparkles className="h-4 w-4 md:h-5 md:w-5 text-primary-foreground" />
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="text-sm md:text-base font-bold text-foreground mb-0.5">
              {t('contentSuggestions.titleInline')}
            </h3>
            <p className={cn(
              "text-xs md:text-sm text-muted-foreground",
              compact ? "leading-snug break-words" : "truncate"
            )}>
              {t('contentSuggestions.suggestionPrefix')}{' '}
              <span className="text-foreground font-medium">{suggestion.title}</span>
              ?
            </p>
          </div>
        </div>

        {/* Right: CTA button - full width on mobile */}
        <Button
          variant="outline"
          className="w-full md:w-auto flex-shrink-0 whitespace-nowrap gap-2 hover:bg-primary hover:text-primary-foreground hover:border-primary transition-colors"
          onClick={handleCreateClick}
        >
          {t('contentSuggestions.createNow')}
          <ArrowRight className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
};

export default ContentSuggestions;
